import moment from 'moment';

// Lightweight TradingStrategy entity wrapper.
// Corresponds to WaveWise.WebApp.Models.Responses.TradingStrategy
export default class TradingStrategy {
  constructor(data) {
    this.Id = data.Id;
    this.Name = data.Name || '';
    this.TradingConnectorId = data.TradingConnectorId;
    this.ModelId = data.ModelId;
    // Signal tag the strategy trades on. Always non-empty on the wire
    // (the controller validates against the model's SignalTags on save
    // and rejects mismatches) but stay null-safe for legacy payloads.
    this.SignalTag = data.SignalTag || '';
    this.Enabled = data.Enabled || false;
    this.UserId = data.UserId;
    this.OwnerName = data.OwnerName || null;
    this.TradingConnectorName = data.TradingConnectorName || null;
    // Connector type/mode are populated server-side via the
    // TradingConnectors LEFT JOIN. Serialised as the enum NAME
    // (e.g. "Alpaca" / "Paper") thanks to StringEnumConverter on the
    // DAL enums. Stay null-safe so legacy payloads don't crash.
    this.TradingConnectorType = data.TradingConnectorType || null;
    this.TradingConnectorMode = data.TradingConnectorMode || null;
    this.ModelName = data.ModelName || null;

    // Portfolio construction (broker symbol only — no local Ticker FK).
    this.MoneyMarketSymbol = data.MoneyMarketSymbol || null;
    this.Stocks = Array.isArray(data.Stocks) ? data.Stocks.map((row) => ({
      Symbol: row.Symbol,
      Percentage: row.Percentage,
    })) : [];

    if (data.CreateTime !== undefined) {
      this.CreateTime = (data.CreateTime === null) ? null : moment.utc(data.CreateTime);
    }
    if (data.UpdateTime !== undefined) {
      this.UpdateTime = (data.UpdateTime === null) ? null : moment.utc(data.UpdateTime);
    }

    // Latest-signal snapshot. Populated server-side at save-time (on create
    // and on update when the model changed). Both fields are either null
    // together (no signal yet) or both set (a signal was computed).
    // The action is the SignalAction enum NAME as a string ("Buy" /
    // "Sell" / "Money") — see SignalAction.StringEnumConverter on the
    // server and the LatestSignalAction VARCHAR column on
    // TradingStrategies.
    this.LatestSignalTime = (data.LatestSignalTime === undefined || data.LatestSignalTime === null)
      ? null
      : moment.utc(data.LatestSignalTime);
    this.LatestSignalAction = (data.LatestSignalAction === undefined || data.LatestSignalAction === null)
      ? null
      : String(data.LatestSignalAction);

    // Trade confirmation
    this.RequireTradeConfirmation = data.RequireTradeConfirmation !== undefined
      ? Boolean(data.RequireTradeConfirmation)
      : true;

    // Pending trade (set when RequireTradeConfirmation=true and signal flipped)
    if (data.PendingTrade) {
      this.PendingTrade = {
        Action: data.PendingTrade.Action || null,
        SignalTime: data.PendingTrade.SignalTime ? moment.utc(data.PendingTrade.SignalTime) : null,
        CreatedAt: data.PendingTrade.CreatedAt ? moment.utc(data.PendingTrade.CreatedAt) : null,
        // Order type snapshotted from the strategy row at the moment the
        // executor created this pending trade. The server serialises the
        // TradingOrderType enum NAME as a string (e.g. "MarketOnClose" /
        // "Market"). Fall back to the strategy's current OrderType when
        // the payload was produced by an older server that didn't snapshot
        // it, so the UI still renders a meaningful value.
        OrderType: data.PendingTrade.OrderType || 'MarketOnClose',
      };
    } else {
      this.PendingTrade = null;
    }

    // Per-trade USD budget. Null = use all available TradableCash (legacy
    // behaviour). When set, the executor uses this fixed amount to size BUYs
    // (stocks + money-market ticker) regardless of how much cash is in the
    // account.
    this.TradeAmountUsd = (data.TradeAmountUsd === undefined || data.TradeAmountUsd === null)
      ? null
      : Number(data.TradeAmountUsd);

    // Auto-adjust flag — when true, the dedicated scheduler updates
    // TradeAmountUsd daily based on realised P&L of recent orders.
    this.AdjustTradeAmountAutomatically = data.AdjustTradeAmountAutomatically !== undefined
      ? Boolean(data.AdjustTradeAmountAutomatically)
      : false;

    // UTC instant the adjuster last wrote a new TradeAmountUsd. Read-only
    // (set server-side by TradingStrategyAmountAdjuster); null until the
    // first adjustment completes.
    this.LastTradeAmountAdjustTime = (data.LastTradeAmountAdjustTime === undefined || data.LastTradeAmountAdjustTime === null)
      ? null
      : moment.utc(data.LastTradeAmountAdjustTime);

    // Admin-only debug flag. When true, this strategy is picked up by the
    // admin's manual "Trading Strategy Execution" trigger on the admin
    // Schedulers page — but only when the strategy is owned by the admin
    // who clicked the button. The daily scheduler ignores this flag and
    // runs every enabled strategy regardless. Defaults to false.
    this.EnableDebugging = data.EnableDebugging !== undefined
      ? Boolean(data.EnableDebugging)
      : false;

    // Whether the executor has already acted on the persisted
    // LatestSignalAction. Flipped to false by the controller whenever a
    // fresh LatestSignalAction is written (on create or when the model's
    // id changes on update), and flipped back to true by the executor
    // after it acts on that initial signal. When false, the next executor
    // run trades on the persisted action regardless of the new run's
    // signal flip. Defaults to true on existing strategies.
    this.InitialSignalTraded = data.InitialSignalTraded !== undefined
      ? Boolean(data.InitialSignalTraded)
      : true;

    // Broker-side order type the executor dispatches per strategy.
    // Server stores the enum NAME as a string
    // ("MarketOnClose" / "Market") thanks to StringEnumConverter on
    // WaveWise.DAL.TradingOrderType. Defaults to MarketOnClose to
    // preserve the historical behaviour; the controller's
    // NOT NULL DEFAULT on the DB column matches this default.
    this.OrderType = data.OrderType || 'MarketOnClose';

    // Time-of-day (Eastern) at which the scheduler triggers this strategy.
    // Server serialises TimeSpan as "HH:mm:ss" (Newtonsoft default).
    // Defaults to "15:30:00" — the historical hard-coded trigger time.
    this.ExecutionTime = data.ExecutionTime || '15:30:00';
  }

  getFormattedLastAdjustTime() {
    return this.LastTradeAmountAdjustTime
      ? this.LastTradeAmountAdjustTime.local().format('YYYY-MM-DD HH:mm:ss')
      : null;
  }

  getFormattedCreateTime() {
    return this.CreateTime ? this.CreateTime.local().format('L LTS') : '';
  }

  getFormattedUpdateTime() {
    return this.UpdateTime ? this.UpdateTime.local().format('L LTS') : '';
  }

  // Date of the latest signal as YYYY-MM-DD (the .sig file only carries
  // MM/dd/yy dates, so we render in the same compact form). Returns null
  // when no signal has been computed yet.
  getFormattedLatestSignalDate() {
    if (!this.LatestSignalTime) return null;
    return this.LatestSignalTime.local().format('YYYY-MM-DD');
  }

  // Short label for the latest-signal action. Matches the labels used in the
  // broker / signal-file download UI (B = Buy, S = Sell, M = Money).
  // Server stores the SignalAction enum NAME as a string.
  getLatestSignalActionLabel() {
    switch ((this.LatestSignalAction || '').toLowerCase()) {
      case 'buy': return 'Buy';
      case 'sell': return 'Sell';
      case 'money': return 'Money';
      default: return this.LatestSignalAction || null;
    }
  }

  // Bootstrap contextual colour for the action badge on the View page.
  // Keep in sync with the SignalAction enum on the server (Buy green,
  // Sell red, Money blue).
  getLatestSignalActionColor() {
    switch ((this.LatestSignalAction || '').toLowerCase()) {
      case 'buy': return 'success';
      case 'sell': return 'danger';
      case 'money': return 'info';
      default: return 'secondary';
    }
  }

  hasLatestSignal() {
    return this.LatestSignalTime != null && this.LatestSignalAction != null;
  }

  // Human-readable label for the strategy's configured OrderType. Server
  // stores the TradingOrderType enum NAME as a string. Unknown values
  // pass through unchanged so a future enum addition surfaces as itself
  // rather than "Unknown".
  getOrderTypeLabel() {
    switch ((this.OrderType || '').toLowerCase()) {
      case 'marketonclose': return 'Market-on-Close';
      case 'market': return 'Market';
      default: return this.OrderType || null;
    }
  }

  // Bootstrap contextual colour for the OrderType badge on the View page
  // and the review modal. Keep in sync with the TradingOrderType enum on
  // the server (Market-on-Close = primary, Market = info).
  getOrderTypeColor() {
    switch ((this.OrderType || '').toLowerCase()) {
      case 'marketonclose': return 'primary';
      case 'market': return 'info';
      default: return 'secondary';
    }
  }

  // Same helpers as above but operating on a PendingTradeInfo snapshot.
  // Used by the Review Pending Trade modal so the user sees exactly what
  // order type will be submitted on approve (snapshotted at the moment
  // the executor created the pending trade — even if the strategy's
  // OrderType has been edited since).
  getPendingTradeOrderTypeLabel() {
    if (!this.PendingTrade) return null;
    switch ((this.PendingTrade.OrderType || '').toLowerCase()) {
      case 'marketonclose': return 'Market-on-Close';
      case 'market': return 'Market';
      default: return this.PendingTrade.OrderType || null;
    }
  }

  getPendingTradeOrderTypeColor() {
    if (!this.PendingTrade) return 'secondary';
    switch ((this.PendingTrade.OrderType || '').toLowerCase()) {
      case 'marketonclose': return 'primary';
      case 'market': return 'info';
      default: return 'secondary';
    }
  }
}
